import { sendGTMEvent } from "@next/third-parties/google";
import { useEffect } from "react";

export const sendEvent = (event: string, options: object = {}) => {
	sendGTMEvent({ event, ...options });
};

export const sendSignUpEvent = () => {
	sendEvent("sign_up", {
		referrer: document.referrer,
		sign_up_method: "email",
	});
};

/**
 * The audience a marketing page targets, sent with `landing_page_view`. The
 * path already identifies the individual page, so these stay broad — see
 * [README.md](./README.md).
 */
export type Icp =
	| "gis"
	| "government"
	| "environment"
	| "partner"
	| "franchise";

/**
 * What kind of public page produced a `public_page_view`. A flat list: the
 * event name already separates marketing from everything else, so this does not
 * need a second tier. Values are permanent once data lands — GA4 cannot rewrite
 * historical parameter values.
 */
export type PageCategory =
	| "case_study"
	| "blog"
	| "tutorial"
	| "beginners_guide"
	| "changelog"
	| "announcement"
	| "legal"
	| "utility";

/**
 * Marketing, solution and comparison pages. The argument is required so a new
 * marketing page cannot be added without declaring its audience.
 */
export const sendLandingPageViewEvent = ({ icp }: { icp: Icp }) => {
	sendEvent("landing_page_view", {
		icp,
		page_path: window.location.pathname,
		page_title: document.title,
		referrer: document.referrer,
	});
};

/**
 * Every other public page — content, legal and utility.
 *
 * Deliberately *not* named `page_view`: that is a GA4 reserved event, already
 * sent automatically by the Google tag on every page load, so reusing it would
 * double-count.
 */
export const sendPublicPageViewEvent = ({
	page_category,
}: {
	page_category: PageCategory;
}) => {
	sendEvent("public_page_view", {
		page_category,
		page_path: window.location.pathname,
		page_title: document.title,
		referrer: document.referrer,
	});
};

export const sendPricingPageViewEvent = () => {
	sendEvent("pricing_page_view", {
		page_path: window.location.pathname,
		page_title: document.title,
		referrer: document.referrer,
	});
};

export const sendSignUpPageViewEvent = () => {
	sendEvent("signup_page_view", {
		page_path: window.location.pathname,
		page_title: document.title,
		referrer: document.referrer,
	});
};

export const sendFranchiseLandingPageViewEvent = () => {
	sendEvent("landing_page_view", {
		icp: "franchise",
		page_path: window.location.pathname,
		page_title: document.title,
		referrer: document.referrer,
	});
};

export const sendFranchiseContactFormSubmitEvent = () => {
	sendEvent("sign_up", {
		sign_up_method: "franchise",
		page_path: window.location.pathname,
		page_title: document.title,
		referrer: document.referrer,
	});
};

export const useSendSignInPageViewEvent = () => {
	useEffect(() => {
		sendEvent("signin_page_view", {
			page_path: window.location.pathname,
			page_title: document.title,
			referrer: document.referrer,
		});
	}, []);
};

export const sendSignInEvent = (email: string) => {
	sendEvent("sign_in", {
		email,
	});
};

export const sendV4ReleaseMessageSeenEvent = () => {
	sendEvent("v4_release_message_seen");
};

export const sendV4ReleaseMessageClosedEvent = () => {
	sendEvent("v4_release_message_closed");
};

export const sendV4ReleaseMessageLinkFollowedEvent = () => {
	sendEvent("v4_release_message_link_followed");
};
